import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { FormProvider, useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import './CustomerWizard.css';
import type { Customer, Corporate } from '../../../services/schemas/customerServiceSchemas';
import { useGetCustomerById, useUpdateCustomer, useCreateCustomer } from '../../../services/customerService';
import Confetti from 'react-confetti';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { validateRegistrationNumber } from '../../../utils/validation/legalEntityType';

// Steps
import CompanyInfoStep from './steps/CompanyInfoStep';
import AddressStep from './steps/AddressStep';
import ContactStep from './steps/ContactStep';
import BankDetailsStep from './steps/BankDetailsStep';
import DirectorsStep from './steps/DirectorsStep';
import ReviewStep from './steps/ReviewStep';

// Form Schema
const customerSchema = z.object({
  customerNumber: z.string().optional(),
  status: z.string().optional(),
  preferredCommChannel: z.string().optional(),
  partyInfo: z.object({
    type: z.literal('CORPORATE').optional(),
    registeredName: z.string().min(1, "Registered name is required"),
    tradingName: z.string().optional(),
    // The legal form decides what a valid registration number is; checked in superRefine below.
    // This page used to accept any text, which is how a loan reference, an ID number and a
    // practice number ended up stored as company registration numbers.
    legalEntityType: z.string().min(1, "Select the legal entity type"),
    // Required per legal form below (validateRegistrationNumber) - blank only for Customer ID (filled in by the service)
    registrationNumber: z.string(),
    industrySector: z.string().optional(),
    taxNumber: z.string().optional(),
    vatNumber: z.string().optional(),
    taxPin: z.string().optional(),
    businessActivity: z.string().optional(),
    representativeOccupation: z.string().optional(),
    annualTurnover: z.preprocess(
      (val) => {
        if (val === '' || val === undefined || val === null || isNaN(Number(val))) {
          return undefined;
        }
        return Number(val);
      },
      z.number().optional()
    ),
    employeeCount: z.preprocess(
      (val) => {
        if (val === '' || val === undefined || val === null || isNaN(Number(val))) {
          return undefined;
        }
        return Number(val);
      },
      z.number().optional()
    ),
    establishmentDate: z.string().optional(),
    incomeType: z.enum(['PO', 'TENDER', 'PRODUCTS_AND_SERVICES', 'OTHER']).optional(),
    incomeDescription: z.string().optional(),
    industryCategory: z.enum(['GOVERNMENT', 'PRIVATE_SECTOR', 'HOUSEHOLD', 'PERSONAL', 'OTHER']).optional(),
    industryDescription: z.string().optional(),
    directors: z.array(
      z.object({
        firstName: z.string().min(1, "First name is required"),
        lastName: z.string().min(1, "Last name is required"),
        idType: z.enum(['SA_ID','PASSPORT']).optional(),
        idNumber: z.string().min(1, "ID number is required"),
        nationality: z.string().optional(),
        dateOfBirth: z.string().optional(),
        designation: z.string().optional(),
        appointmentDate: z.string().optional(),
        gender: z.enum(['MALE','FEMALE','OTHER','PREFER_NOT-TO-SAY']).optional(),
        race: z.enum(['BLACK','COLOURED','WHITE','ASIAN','OTHER','PREFER_NOT-TO-SAY']).optional(),
        isDisabled: z.boolean().optional(),
        isPreviouslyDisadvantaged: z.boolean().optional(),
        ownershipPercentage: z.preprocess(
          (val) => {
            if (val === '' || val === undefined || val === null || isNaN(Number(val))) {
              return undefined;
            }
            return Number(val);
          },
          z.number().optional()
        ),
        isExecutive: z.boolean().optional(),
        isCommunicationContact: z.boolean().optional()
      })
    ).default([]),
  }).superRefine((partyInfo, ctx) => {
    const result = validateRegistrationNumber(partyInfo.registrationNumber, partyInfo.legalEntityType);
    if (result !== true) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: result, path: ['registrationNumber'] });
    }
  }),
  addresses: z.array(
    z.object({
      type: z.string().min(1, "Address type is required"),
      line1: z.string().min(1, "Address line 1 is required"),
      line2: z.string().optional(),
      city: z.string().min(1, "City is required"),
      postalCode: z.string().min(1, "Postal code is required"),
      province: z.string().min(1, "Province is required"),
      country: z.string().min(1, "Country is required").default("South Africa"),
      isPrimary: z.boolean().optional(),
    })
  ).default([]),
  contacts: z.array(z.object({
    contactType: z.enum(['EMAIL', 'SMS', 'WHATSAPP', 'MOBILE']),
    contactValue: z.string().min(1, 'Contact value is required'),
    primary: z.boolean().optional(),
  })),
  bankDetails: z.array(z.object({
    accountType: z.string(),
    bankName: z.string().min(1, 'Bank name is required'),
    accountNumber: z.string().min(1, 'Account number is required'),
    branchCode: z.string().optional(),
    accountHolderName: z.string().min(1, 'Account holder name is required'),
  })),
});

export type FormValues = z.infer<typeof customerSchema>;

const steps = [
  { label: 'Company Info', completed: false },
  { label: 'Addresses', completed: false },
  { label: 'Contacts', completed: false },
  { label: 'Bank Details', completed: false },
  { label: 'Directors', completed: false },
  { label: 'Review', completed: false },
];

export default function CustomerWizard() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [activeStep, setActiveStep] = useState(0);
  const [isSubmitSuccess, setIsSubmitSuccess] = useState(false);
  const [clientFullScreen, setClientFullScreen] = useState(false);
  const [windowDimension, setWindowDimension] = useState({
    width: window.innerWidth,
    height: window.innerHeight,
  });

  const isEditMode = !!id;

  // Fetch customer data if in edit mode
  const { data: customerData, isLoading, error } = useGetCustomerById(
    id || '',
    {
      query: {
        enabled: isEditMode && !!id
      }
    }
  );

  // Add update mutation
  const updateMutation = useUpdateCustomer();
  const createMutation = useCreateCustomer();

  const methods = useForm<FormValues>({
    resolver: zodResolver(customerSchema),
    mode: 'onChange',
    defaultValues: {
      customerNumber: '',
      status: 'ACTIVE',
      preferredCommChannel: 'EMAIL',
      partyInfo: {
        type: 'CORPORATE',
        registeredName: '',
        tradingName: '',
        legalEntityType: '',
        registrationNumber: '',
        industrySector: '',
        taxNumber: '',
        vatNumber: '',
        taxPin: '',
        businessActivity: '',
        representativeOccupation: '',
        annualTurnover: undefined,
        employeeCount: undefined,
        establishmentDate: '',
        incomeType: undefined,
        incomeDescription: '',
        industryCategory: undefined,
        industryDescription: '',
        directors: [{
          firstName: '',
          lastName: '',
          idType: undefined as any,
          idNumber: '',
          nationality: '',
          dateOfBirth: '',
          designation: '',
          appointmentDate: '',
          gender: undefined as any,
          race: undefined as any,
          isDisabled: false,
          isPreviouslyDisadvantaged: false,
          ownershipPercentage: 0,
          isExecutive: false,
          isCommunicationContact: false
        }],
      },
      addresses: [],
      contacts: [],
      bankDetails: [],
    },
  });

  useEffect(() => {
    if (customerData) {
      // Get first director's designation for representativeOccupation if not provided
      const firstDirector = (customerData.info as Corporate)?.directors?.[0];
      const representativeOccupation = (customerData.info as Corporate)?.representativeOccupation 
        || firstDirector?.designation 
        || '';
      
      // Transform the customer data to match our form structure
      const formData: FormValues = {
        customerNumber: customerData.customerNumber || '',
        status: customerData.status || 'ACTIVE',
        preferredCommChannel: customerData.preferredCommChannel || 'EMAIL',
        partyInfo: {
          ...customerData.info as Corporate,
          // Customers created before the legal form existed have none; '' makes the edit ask for it.
          legalEntityType: (customerData.info as any)?.legalEntityType || '',
          representativeOccupation: representativeOccupation,
          directors: (customerData.info as Corporate).directors?.map(director => ({
            firstName: director.firstName || '',
            lastName: director.lastName || '',
            // UI-only: we don't get idType from API; leave undefined
            idType: undefined as any,
            idNumber: director.idNumber || '',
            nationality: director.nationality,
            dateOfBirth: director.dateOfBirth,
            designation: director.designation,
            appointmentDate: (director as any).appointmentDate || '',
            gender: (director as any).gender as any,
            race: (director as any).race as any,
            isDisabled: (director as any).isDisabled ?? false,
            isPreviouslyDisadvantaged: (director as any).isPreviouslyDisadvantaged ?? false,
            ownershipPercentage: director.ownershipPercentage,
            isExecutive: director.isExecutive,
            isCommunicationContact: (director as any).isCommunicationContact ?? false
          })) || [],
        },
        // Map API address schema to form schema
        addresses: (customerData.addresses || []).map((addr: any) => ({
          type: addr.addressType || '',
          line1: addr.streetAddress || '',
          line2: addr.suburb || '',
          city: addr.city || '',
          postalCode: addr.postalCode || '',
          province: addr.stateProvince || '',
          country: addr.country || 'South Africa',
          isPrimary: addr.isPrimary || false
        })),
        // Map API contacts schema to form schema
        contacts: (customerData.contacts || []).map((contact: any) => ({
          contactType: contact.contactType || 'EMAIL',
          contactValue: contact.contactValue || '',
          primary: contact.primary || false
        })),
        // Map API bankDetails schema to form schema
        bankDetails: (customerData.bankDetails || []).map((bank: any) => ({
          accountType: bank.accountType || '',
          bankName: bank.bankName || '',
          accountNumber: bank.accountNumber || '',
          branchCode: bank.branchCode || '',
          accountHolderName: bank.accountHolderName || ''
        })),
      };
      methods.reset(formData);
    }
  }, [customerData, methods]);

  const handleNext = async () => {
    try {
      // Validate only the current step fields
      let result = false;

      // Validate based on the current step
      switch (activeStep) {
        case 0:
          // Company Info step
          result = await methods.trigger(['partyInfo.registeredName', 'partyInfo.legalEntityType', 'partyInfo.registrationNumber']);
          break;
        case 1:
          // Addresses step
          result = await methods.trigger('addresses');
          break;
        case 2:
          // Contacts step
          result = await methods.trigger('contacts');
          break;
        case 3:
          // Bank Details step
          result = await methods.trigger('bankDetails');
          break;
        case 4:
          // Directors step
          result = await methods.trigger('partyInfo.directors');
          break;
        default:
          result = true;
          break;
      }

      console.log('Validation result:', result);

      if (!result) {
        console.log('Validation failed, form errors:', methods.formState.errors);
        return;
      }

      // Move to next step
      setActiveStep((prevActiveStep) => prevActiveStep + 1);
      console.log('Moving to step:', activeStep + 1);
    } catch (err) {
      console.error('Error in handleNext:', err);
      methods.setError('root', {
        type: 'manual',
        message: 'An error occurred. Please try again.'
      });
    }
  };

  const handleBack = () => {
    if (activeStep > 0) {
      setActiveStep(prev => prev - 1);
    }
  };

  const handleSubmit = async (data: FormValues) => {
    console.log('handleSubmit called with data:', data);
    try {
      // Clean the data before validation
      const cleanedData = {
        ...data,
        partyInfo: {
          ...data.partyInfo,
          annualTurnover: typeof data.partyInfo.annualTurnover === 'string' && data.partyInfo.annualTurnover === '' || isNaN(Number(data.partyInfo.annualTurnover))
            ? undefined
            : Number(data.partyInfo.annualTurnover),
          employeeCount: typeof data.partyInfo.employeeCount === 'string' && data.partyInfo.employeeCount === '' || isNaN(Number(data.partyInfo.employeeCount))
            ? undefined
            : Number(data.partyInfo.employeeCount),
          directors: data.partyInfo.directors?.map(director => ({
            ...director,
            ownershipPercentage: typeof director.ownershipPercentage === 'string' && director.ownershipPercentage === '' || isNaN(Number(director.ownershipPercentage))
              ? undefined
              : Number(director.ownershipPercentage)
          }))
        }
      };

      // Validate the entire form before proceeding
      const validationResult = customerSchema.safeParse(cleanedData);
      if (!validationResult.success) {
        console.error('Validation failed:', validationResult.error);
        methods.setError('root', {
          type: 'manual',
          message: 'Please check all required fields and try again.'
        });
        return;
      }

      console.log('Form submitted with cleaned data:', cleanedData);
      console.log('Form validation state:', methods.formState);

      // Map the form data to the expected API schema format
      const mappedAddresses = cleanedData.addresses.map(addr => ({
        addressType: addr.type,
        streetAddress: addr.line1,
        suburb: addr.line2,
        city: addr.city,
        stateProvince: addr.province,
        postalCode: addr.postalCode,
        country: addr.country,
        isPrimary: addr.isPrimary
      }));

      // Map contacts data to API structure
      const mappedContacts = cleanedData.contacts.map(contact => ({
        contactType: contact.contactType,
        contactValue: contact.contactValue,
        primary: contact.primary
      }));

      // Map bank details data to API structure
      const mappedBankDetails = cleanedData.bankDetails.map(bank => ({
        accountType: bank.accountType,
        bankName: bank.bankName,
        accountNumber: bank.accountNumber,
        branchCode: bank.branchCode,
        accountHolderName: bank.accountHolderName
      }));

      // Map directors data to API structure
      const mappedDirectors = cleanedData.partyInfo.directors?.map(director => ({
        firstName: director.firstName,
        lastName: director.lastName,
        idNumber: director.idNumber,
        nationality: director.nationality,
        dateOfBirth: director.dateOfBirth,
        designation: director.designation,
        appointmentDate: director.appointmentDate,
        gender: director.gender,
        race: director.race,
        isDisabled: director.isDisabled,
        isPreviouslyDisadvantaged: director.isPreviouslyDisadvantaged,
        ownershipPercentage: director.ownershipPercentage,
        isExecutive: director.isExecutive,
        isCommunicationContact: director.isCommunicationContact
      })) || [];

      if (isEditMode && id) {
        console.log('Updating customer with ID:', id);
        const updateData = {
          id: id,
          type: 'CORPORATE',
          status: cleanedData.status,
          preferredCommChannel: cleanedData.preferredCommChannel,
          customerNumber: cleanedData.customerNumber,
          info: {
            type: 'CORPORATE',
            registeredName: cleanedData.partyInfo.registeredName,
            tradingName: cleanedData.partyInfo.tradingName,
            legalEntityType: cleanedData.partyInfo.legalEntityType || undefined,
            registrationNumber: cleanedData.partyInfo.registrationNumber,
            industrySector: cleanedData.partyInfo.industrySector,
            taxNumber: cleanedData.partyInfo.taxNumber,
            vatNumber: cleanedData.partyInfo.vatNumber,
            taxPin: cleanedData.partyInfo.taxPin,
            businessActivity: cleanedData.partyInfo.businessActivity,
            representativeOccupation: cleanedData.partyInfo.representativeOccupation,
            annualTurnover: cleanedData.partyInfo.annualTurnover,
            employeeCount: cleanedData.partyInfo.employeeCount,
            establishmentDate: cleanedData.partyInfo.establishmentDate,
            incomeType: cleanedData.partyInfo.incomeType,
            incomeDescription: cleanedData.partyInfo.incomeDescription,
            industryCategory: cleanedData.partyInfo.industryCategory,
            industryDescription: cleanedData.partyInfo.industryDescription,
            directors: mappedDirectors
          },
          addresses: mappedAddresses,
          contacts: mappedContacts,
          bankDetails: mappedBankDetails
        } as unknown as Customer;
        console.log('Update payload:', updateData);

        const result = await updateMutation.mutateAsync({
          customerId: id || '',
          data: updateData
        });
        console.log('Update successful:', result);
        toast.success('Customer updated successfully');
        setIsSubmitSuccess(true);
      } else {
        console.log('Creating new customer');
        const createData = {
          type: 'CORPORATE',
          status: cleanedData.status,
          preferredCommChannel: cleanedData.preferredCommChannel,
          customerNumber: cleanedData.customerNumber,
          info: {
            type: 'CORPORATE',
            registeredName: cleanedData.partyInfo.registeredName,
            tradingName: cleanedData.partyInfo.tradingName,
            legalEntityType: cleanedData.partyInfo.legalEntityType || undefined,
            registrationNumber: cleanedData.partyInfo.registrationNumber,
            industrySector: cleanedData.partyInfo.industrySector,
            taxNumber: cleanedData.partyInfo.taxNumber,
            vatNumber: cleanedData.partyInfo.vatNumber,
            taxPin: cleanedData.partyInfo.taxPin,
            businessActivity: cleanedData.partyInfo.businessActivity,
            representativeOccupation: cleanedData.partyInfo.representativeOccupation,
            annualTurnover: cleanedData.partyInfo.annualTurnover,
            employeeCount: cleanedData.partyInfo.employeeCount,
            establishmentDate: cleanedData.partyInfo.establishmentDate,
            incomeType: cleanedData.partyInfo.incomeType,
            incomeDescription: cleanedData.partyInfo.incomeDescription,
            industryCategory: cleanedData.partyInfo.industryCategory,
            industryDescription: cleanedData.partyInfo.industryDescription,
            directors: mappedDirectors
          },
          addresses: mappedAddresses,
          contacts: mappedContacts,
          bankDetails: mappedBankDetails
        } as unknown as Customer;
        console.log('Create payload:', createData);

        const result = await createMutation.mutateAsync({
          data: createData
        });
        console.log('Create successful:', result);
        toast.success('Customer created successfully');
        setIsSubmitSuccess(true);
      }
    } catch (err) {
      console.error('Error updating/creating customer:', err);
      toast.error((err as any)?.message || 'Failed to save customer');
      // Show error to user
      methods.setError('root', {
        type: 'manual',
        message: `Failed to ${isEditMode ? 'update' : 'create'} customer. Please try again.`
      });
    }
  };

  // Add this useEffect to debug form state
  useEffect(() => {
    const subscription = methods.watch((value) => {
      console.log('Form values changed:', value);
    });
    return () => subscription.unsubscribe();
  }, [methods]);

  const handleFullScreen = () => {
    setClientFullScreen(!clientFullScreen);
    if (!clientFullScreen) {
      setWindowDimension({
        width: window.innerWidth,
        height: window.innerHeight,
      });
    }
  };

  const getStepTitle = (step: number) => {
    switch (step) {
      case 0:
        return 'Company Information';
      case 1:
        return 'Addresses';
      case 2:
        return 'Contacts';
      case 3:
        return 'Bank Details';
      case 4:
        return 'Directors';
      case 5:
        return 'Review';
      default:
        return '';
    }
  };

    const getStepContent = (step: number) => {
    switch (step) {
      case 0:
        return <CompanyInfoStep />;
      case 1:
        return <AddressStep />;
      case 2:
        return <ContactStep />;
      case 3:
        return <BankDetailsStep />;
      case 4:
        return <DirectorsStep />;
      case 5:
        return <ReviewStep />;
      default:
        return null;
    }
  };

  // Compute a display name for the header
  const registeredName = methods.watch('partyInfo.registeredName');
  const tradingName = methods.watch('partyInfo.tradingName');
  const customerNumber = methods.watch('customerNumber');
  const displayName = (tradingName && tradingName.trim()) || (registeredName && registeredName.trim()) || (customerNumber ? `Customer #${customerNumber}` : 'Customer');


  // Display loading state
  if (isLoading) {
    return (
      <div className="customer-wizard">
        <div className="container-fluid h-100 d-flex align-items-center justify-content-center">
          <div className="text-center">
            <div className="spinner-border" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
            <p className="mt-3">Loading customer details...</p>
          </div>
        </div>
      </div>
    );
  }

  // Display error state
  if (error) {
    return (
      <div className="customer-wizard">
        <div className="container-fluid h-100 d-flex align-items-center justify-content-center">
          <div className="alert alert-danger">
            <i className="bi bi-exclamation-triangle me-2"></i>
            {(error as { message?: string })?.message || 'Failed to load customer details'}
          </div>
        </div>
      </div>
    );
  }

  // Display submission success state
  if (isSubmitSuccess) {
    return (
      <div className="container-fluid p-4">
        {clientFullScreen && (
          <Confetti
            width={windowDimension.width}
            height={windowDimension.height}
            recycle={false}
            numberOfPieces={500}
          />
        )}
        <div className="card">
          <div className="card-body text-center py-5">
            <div className="mb-4">
              <i className="bi bi-check-circle text-success" style={{ fontSize: '4rem' }}></i>
            </div>
            <h2 className="mb-3">Customer {isEditMode ? 'Updated' : 'Created'} Successfully!</h2>
            <p className="text-muted mb-4">
              The customer details have been {isEditMode ? 'updated' : 'created'} successfully.
            </p>
            <button
              className="btn btn-primary mt-3"
              onClick={() => navigate('/loan-customers')}
            >
              <i className="bi bi-arrow-left me-2"></i>
              Return to Customers List
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`customer-wizard ${clientFullScreen ? 'fullscreen' : ''}`}>
      <ToastContainer position="top-right" autoClose={4000} hideProgressBar />
      <div className="container-fluid h-100">
        <div className="row h-100">
          {/* Main content */}
          <div className="col-12 col-lg-9 main-content">
            <div className="card">
              <div className="card-header d-flex justify-content-between align-items-center">
                <div className="d-flex align-items-center gap-3">
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-secondary"
                    onClick={() => navigate('/loan-customers')}
                    title="Back to Customer List"
                  >
                    <i className="bi bi-arrow-left me-1"></i>
                    Back
                  </button>
                                    <h5 className="card-title mb-0 d-flex align-items-center gap-2 flex-wrap">
                    <span>{isEditMode ? 'Edit Customer' : 'New Customer'}</span>
                    <span className="text-muted">Step {activeStep + 1} of {steps.length}</span>
                    <span className="badge badge-enhanced badge-primary-gradient ms-2">{displayName}</span>
                  </h5>

                </div>
                <div>
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-secondary me-2"
                    onClick={handleFullScreen}
                  >
                    <i className={`bi ${clientFullScreen ? 'bi-fullscreen-exit' : 'bi-fullscreen'}`}></i>
                  </button>
                </div>
              </div>
              <div className="card-body">
                <FormProvider {...methods}>
                  <form>
                    {methods.formState.errors.root && (
                      <div className="alert alert-danger mb-4">
                        {methods.formState.errors.root.message}
                      </div>
                    )}
                    {getStepContent(activeStep)}
                    <div className="d-flex justify-content-between mt-4">
                      <button
                        type="button"
                        className="btn btn-outline-secondary"
                        disabled={activeStep === 0}
                        onClick={handleBack}
                      >
                        Back
                      </button>
                      <div>
                        {activeStep === steps.length - 1 ? (
                          <button
                            type="button"
                            className="btn btn-primary"
                            disabled={methods.formState.isSubmitting || updateMutation.isPending || createMutation.isPending}
                            onClick={methods.handleSubmit(handleSubmit)}
                          >
                            {methods.formState.isSubmitting || updateMutation.isPending || createMutation.isPending ? (
                              <>
                                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                {isEditMode ? 'Updating...' : 'Creating...'}
                              </>
                            ) : (
                              isEditMode ? 'Update Customer' : 'Create Customer'
                            )}
                          </button>
                        ) : (
                          <button
                            type="button"
                            className="btn btn-primary"
                            onClick={handleNext}
                          >
                            Next
                          </button>
                        )}
                      </div>
                    </div>
                  </form>
                </FormProvider>
              </div>
            </div>
          </div>

          {/* Sidebar */}
          <div className="col-lg-3 d-none d-lg-block">
            <div className="sidebar always-visible">
              <div className="sidebar-content">
                <div className="sidebar-header">
                  <h4>{isEditMode ? 'Edit Progress' : 'New Customer Progress'}</h4>
                  <p className="text-muted">Complete all steps to {isEditMode ? 'update' : 'create'} customer</p>
                </div>
                <div className="steps-container">
                  {steps.map((step, index) => (
                    <div
                      key={index}
                      className={`step-item ${activeStep === index ? 'active' : ''} ${
                        activeStep > index ? 'completed' : ''
                      }`}
                    >
                      <div className="step-number">
                        {activeStep > index ? (
                          <i className="bi bi-check"></i>
                        ) : (
                          index + 1
                        )}
                      </div>
                      <div className="step-label">{step.label}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
